/**
 * Sums chip and hold-code counts across every bucket in a
 * `dashboard.bucketBreakdowns` map. Used for the "All" bucket view
 * where the frontend needs a single chip bar that reflects the
 * combined distribution across RELEASABLE + LOCKED + READY_TO_ALLOCATE
 * + AWAITING_STOCK.
 *
 * Since every order lives in exactly one bucket, per-chip counts are
 * additive — e.g. `CRED_all = CRED_releasable + CRED_locked + CRED_ready
 * + CRED_awaiting`. Same for individual hold-code counts inside each
 * chip. Labels, codes and member-hold metadata are copied from the
 * first breakdown that defines them; the backend taxonomy is the
 * authoritative source of chip and hold-code ordering.
 *
 * @param {Record<string, object> | null | undefined} bucketBreakdowns
 *   The `dashboard.bucketBreakdowns` map from the API response.
 * @returns {object | null} An aggregated breakdown shaped exactly
 *   like a single `BucketChipBreakdown`, or `null` when the input is
 *   empty or missing.
 */
export function aggregateBucketBreakdowns(bucketBreakdowns) {
  if (!bucketBreakdowns) return null
  const breakdowns = Object.values(bucketBreakdowns).filter(Boolean)
  if (breakdowns.length === 0) return null

  // Use the first breakdown as the structural template. All backend
  // breakdowns have the same ordered chip list because they are built
  // from the same catalog.
  const [template] = breakdowns
  const aggregatedChips = (template.primaryChips || []).map((chipTemplate) => {
    const chipCode = chipTemplate.code
    const chipCount = breakdowns.reduce((sum, b) => {
      const match = (b.primaryChips || []).find((c) => c.code === chipCode)
      return sum + (match ? match.count || 0 : 0)
    }, 0)

    // Walk the template's hold list to preserve ordering, summing
    // the same hold code across every breakdown's matching chip.
    const orderedHolds = (chipTemplate.holds || []).map((holdTemplate) => {
      const holdCount = breakdowns.reduce((sum, b) => {
        const matchChip = (b.primaryChips || []).find((c) => c.code === chipCode)
        if (!matchChip) return sum
        const matchHold = (matchChip.holds || []).find((h) => h.code === holdTemplate.code)
        return sum + (matchHold ? matchHold.count || 0 : 0)
      }, 0)
      return {
        code: holdTemplate.code,
        description: holdTemplate.description,
        count: holdCount,
      }
    })

    return {
      code: chipTemplate.code,
      label: chipTemplate.label,
      count: chipCount,
      holds: orderedHolds,
    }
  })

  return {
    orderCount: breakdowns.reduce((sum, b) => sum + (b.orderCount || 0), 0),
    primaryChips: aggregatedChips,
    secondaryChips: [],
    additionalHolds: [],
  }
}
